"use client";

import { useRef } from "react";

import { liveHomeCopy as enLiveHomeCopy } from "@/content/pages/en/live-home";
import { cn } from "@/lib/utils";

import { CardLede } from "./card-lede";
import { GUARANTEES_DURATION_MS, GuaranteesMock } from "./guarantees-card";
import type { TermsCopy } from "./types";
import { useGuaranteesPlaybackPhase } from "./use-guarantees-playback-phase";
import { useTermsCardMotion } from "./use-terms-card-motion";
import type { TermsMotionDebugProps } from "./use-terms-card-motion";

export interface AnimatedGuaranteesCardProps {
  copy?: TermsCopy;
  className?: string;
  motionDebug?: TermsMotionDebugProps;
}

export function AnimatedGuaranteesCard({
  copy = enLiveHomeCopy.terms,
  className,
  motionDebug,
}: AnimatedGuaranteesCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { state, phase, reducedMotion } = useTermsCardMotion(
    ref,
    GUARANTEES_DURATION_MS,
    motionDebug
  );
  const effectivePhase = useGuaranteesPlaybackPhase(
    state,
    phase,
    reducedMotion,
    GUARANTEES_DURATION_MS
  );
  const mockPhase =
    phase ?? (state === "complete" ? undefined : effectivePhase);

  return (
    <div
      ref={ref}
      data-testid="terms-guarantees-animated"
      className={cn(
        "border-grey-100 bg-grey-0 relative h-[280px] overflow-hidden rounded-2xl border p-6 lg:p-8",
        className
      )}
    >
      <CardLede
        lead={copy.guaranteesLead}
        rest={copy.guaranteesRest}
        className="max-w-[240px] lg:max-w-[309px]"
      />

      <GuaranteesMock copy={copy} motionState={state} phase={mockPhase} />
    </div>
  );
}
